import { useMemo, useState } from 'react'

import * as api from 'api/api'
import { useProject } from 'components/Layout'
import { Button, Card, EmptyState, ErrorState, SelectField, Spinner, StatusBadge, TextField } from 'components/ui'
import {
  STATUS_ORDER,
  addDays,
  formatDate,
  formatNumber,
  monthBounds,
  monthKey,
  monthTitle,
  monthsBetween,
  weekStart,
} from 'lib/format'
import { t } from 'lib/i18n'
import { useAction, useAsync } from 'lib/hooks'

type Mode = 'week' | 'month' | 'custom'

const clampDate = (date: string, min: string, max: string) => (date < min ? min : date > max ? max : date)

export const SummariesPage = () => {
  const { project } = useProject()
  const { data: today } = useAsync(api.fetchToday, [])
  const anchor = clampDate(today ?? project.startDate, project.startDate, project.endDate)
  const months = useMemo(() => monthsBetween(project.startDate, project.endDate), [project.startDate, project.endDate])

  const [mode, setMode] = useState<Mode>('month')
  const [month, setMonth] = useState<string | null>(null)
  const [week, setWeek] = useState<string | null>(null)
  const [customFrom, setCustomFrom] = useState(project.startDate)
  const [customTo, setCustomTo] = useState(project.endDate)
  const { busy, run } = useAction()

  const weeks = useMemo(() => {
    const list: string[] = []
    for (let day = weekStart(project.startDate); day <= project.endDate; day = addDays(day, 7)) list.push(day)
    return list
  }, [project.startDate, project.endDate])

  const activeMonth = month ?? monthKey(anchor)
  const activeWeek = week ?? weekStart(anchor)

  const range = (() => {
    if (mode === 'month') return monthBounds(activeMonth)
    if (mode === 'week') return { from: activeWeek, to: addDays(activeWeek, 6) }
    return { from: customFrom, to: customTo }
  })()
  const from = clampDate(range.from, project.startDate, project.endDate)
  const to = clampDate(range.to, project.startDate, project.endDate)
  const invalid = !from || !to || to < from

  const { data, error, loading, reload } = useAsync(
    () => (invalid ? Promise.resolve(undefined) : api.fetchSummary(project.id, from, to)),
    [project.id, from, to, invalid]
  )

  const counts = new Map(data?.statusCounts.map((item) => [item.status, item.count]))

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('الملخصات', 'Summaries')}</h1>
          <p className="muted">
            {invalid
              ? t('اختر فترة صحيحة', 'Choose a valid period')
              : t(`الفترة من ${formatDate(from)} إلى ${formatDate(to)}`, `From ${formatDate(from)} to ${formatDate(to)}`)}
          </p>
        </div>
        <div className="toolbar">
          <Button
            icon="download"
            disabled={invalid}
            busy={busy === 'xlsx'}
            onClick={() => run('xlsx', () => api.downloadRange(project.id, from, to, 'xlsx'), t('تم تجهيز الملف', 'File is ready'))}
          >
            {t('تقارير الفترة Excel', 'Excel for the period')}
          </Button>
          <Button
            icon="file"
            disabled={invalid}
            busy={busy === 'pdf'}
            onClick={() => run('pdf', () => api.downloadRange(project.id, from, to, 'pdf'), t('تم تجهيز الملف', 'File is ready'))}
          >
            {t('تقارير الفترة PDF', 'PDF for the period')}
          </Button>
        </div>
      </div>

      <Card>
        <div className="filters">
          <div className="segmented" role="group" aria-label={t('نوع الفترة', 'Period type')}>
            <button type="button" aria-pressed={mode === 'week'} onClick={() => setMode('week')}>
              {t('أسبوعي', 'Weekly')}
            </button>
            <button type="button" aria-pressed={mode === 'month'} onClick={() => setMode('month')}>
              {t('شهري', 'Monthly')}
            </button>
            <button type="button" aria-pressed={mode === 'custom'} onClick={() => setMode('custom')}>
              {t('فترة مخصصة', 'Custom period')}
            </button>
          </div>
          {mode === 'month' ? (
            <SelectField label={t('الشهر', 'Month')} value={activeMonth} onChange={(event) => setMonth(event.target.value)}>
              {months.map((key) => (
                <option key={key} value={key}>
                  {monthTitle(key)}
                </option>
              ))}
            </SelectField>
          ) : mode === 'week' ? (
            <SelectField label={t('الأسبوع (السبت – الجمعة)', 'Week (Saturday – Friday)')} value={activeWeek} onChange={(event) => setWeek(event.target.value)}>
              {weeks.map((start, index) => (
                <option key={start} value={start}>
                  {t(
                    `الأسبوع ${index + 1}: ${formatDate(start)} – ${formatDate(addDays(start, 6))}`,
                    `Week ${index + 1}: ${formatDate(start)} – ${formatDate(addDays(start, 6))}`
                  )}
                </option>
              ))}
            </SelectField>
          ) : (
            <>
              <TextField
                label={t('من تاريخ', 'From')}
                type="date"
                min={project.startDate}
                max={project.endDate}
                value={customFrom}
                onChange={(event) => setCustomFrom(event.target.value)}
              />
              <TextField
                label={t('إلى تاريخ', 'To')}
                type="date"
                min={project.startDate}
                max={project.endDate}
                value={customTo}
                onChange={(event) => setCustomTo(event.target.value)}
                error={invalid ? t('تاريخ النهاية قبل البداية', 'The end date is before the start date') : undefined}
              />
            </>
          )}
        </div>
      </Card>

      {invalid ? null : loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : data ? (
        <>
          <div className="stats stats--static">
            <div className="stat">
              <span className="stat__value">{data.totalDays}</span>
              <span className="stat__label">{t('أيام الفترة', 'Days in the period')}</span>
            </div>
            <div className="stat">
              <span className="stat__value">{data.workingDays}</span>
              <span className="stat__label">{t('أيام عمل فعلية', 'Working days')}</span>
            </div>
            <div className="stat">
              <span className="stat__value">{data.visits}</span>
              <span className="stat__label">{t('زيارات', 'Visits')}</span>
            </div>
            <div className="stat">
              <span className="stat__value">{data.notes}</span>
              <span className="stat__label">{t('ملاحظات إشراف', 'Supervision notes')}</span>
            </div>
            <div className="stat">
              <span className="stat__value">{data.attachments}</span>
              <span className="stat__label">{t('مرفقات', 'Attachments')}</span>
            </div>
          </div>

          <Card title={t('حالة التقارير', 'Report status')}>
            <ul className="status-list">
              {STATUS_ORDER.map((status) => (
                <li key={status}>
                  <StatusBadge status={status} />
                  <strong className="num">{counts.get(status) ?? 0}</strong>
                </li>
              ))}
            </ul>
          </Card>

          <div className="summary-grid">
            <Card title={t('المواد الموردة', 'Delivered materials')}>
              {data.materials.length ? (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">{t('المادة', 'Material')}</th>
                        <th scope="col">{t('مرات التوريد', 'Deliveries')}</th>
                        <th scope="col">{t('الإجمالي', 'Total')}</th>
                        <th scope="col">{t('الكميات', 'Quantities')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.materials.map((item) => (
                        <tr key={item.name}>
                          <td>{item.name}</td>
                          <td className="num">
                            {item.deliveries} ({t(`${item.days} يوم`, `${item.days} days`)})
                          </td>
                          <td className="num">
                            {item.total != null ? `${formatNumber(item.total)} ${item.unit ?? ''}` : <span className="muted">—</span>}
                          </td>
                          <td className="small">{item.quantities.filter(Boolean).join(t('، ', ', ')) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState title={t('لا توجد مواد في هذه الفترة', 'No materials in this period')} />
              )}
            </Card>

            <Card title={t('الكوادر (يوم-عامل)', 'Crew (person-days)')}>
              {data.staff.length ? (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">{t('المسمى', 'Job title')}</th>
                        <th scope="col">{t('يوم-عامل', 'Person-days')}</th>
                        <th scope="col">{t('أيام الحضور', 'Days present')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.staff.map((item) => (
                        <tr key={item.title}>
                          <td>{item.title}</td>
                          <td className="num">{item.personDays}</td>
                          <td className="num">{item.days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState title={t('لا توجد كوادر مسجلة', 'No crew recorded')} icon="users" />
              )}
            </Card>

            <Card title={t('المعدات (يوم-آلة)', 'Equipment (machine-days)')}>
              {data.equipment.length ? (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">{t('الآلة', 'Machine')}</th>
                        <th scope="col">{t('يوم-آلة', 'Machine-days')}</th>
                        <th scope="col">{t('أيام التشغيل', 'Days in use')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.equipment.map((item) => (
                        <tr key={item.name}>
                          <td>{item.name}</td>
                          <td className="num">{item.unitDays}</td>
                          <td className="num">{item.days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState title={t('لا توجد معدات مسجلة', 'No equipment recorded')} />
              )}
            </Card>

            <Card title={t('الفحوصات المخبرية', 'Lab tests')}>
              {data.labTests.length ? (
                <ul className="status-list">
                  {data.labTests.map((item) => (
                    <li key={item.testType}>
                      <span>{item.testType}</span>
                      <strong className="num">{item.count}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title={t('لا توجد فحوصات في هذه الفترة', 'No lab tests in this period')} />
              )}
            </Card>
          </div>
        </>
      ) : null}
    </div>
  )
}
